import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Checkbox,
    Grid,
    Typography,
    Stack
} from '@mui/material';
import DialogSelectFanpages from '../Fanpages/Posts/Components/DialogSelectFanpages';
import { useTranslation } from "react-i18next";
import SelectGroupFanpage from '../AssistantTraining/Components/SelectGroupFanpage';
import Swal from 'sweetalert2';
import DialogSelectGroupFanpages from './components/DialogSelectGroupFanpage';

// Cấu hình các ngày trong tuần (API: 2 -> 8)
const DAYS_OF_WEEK = [
    { id: 'mon', label: 'Thứ 2', value: 2 },
    { id: 'tue', label: 'Thứ 3', value: 3 },
    { id: 'wed', label: 'Thứ 4', value: 4 },
    { id: 'thu', label: 'Thứ 5', value: 5 },
    { id: 'fri', label: 'Thứ 6', value: 6 },
    { id: 'sat', label: 'Thứ 7', value: 7 },
    { id: 'sun', label: 'Chủ nhật', value: 8 },
];

const DEFAULT_SCHEDULE = DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day.id] = { active: false, start: '00:00', end: '23:00' };
    return acc;
}, {});

export default function AiFormDialog({ 
    open, 
    handleClose, 
    initialData, 
    onSubmit, 
    state, 
    handleChangeFanpage, 
    handleSelectGroupFanpageChange 
}) {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [replyMode, setReplyMode] = useState('always'); // 'always' (0) hoặc 'custom' (1)
    const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);

    // Xử lý đổ dữ liệu khi Mở Dialog (Edit hoặc Create)
    useEffect(() => {
        if (open) {
            if (initialData) {
                // TRƯỜNG HỢP CHỈNH SỬA
                setName(initialData.name || '');
                setReplyMode(initialData.time_type === 0 ? 'always' : 'custom');

                // Map dữ liệu setting_times từ API vào object schedule của UI
                const newSchedule = JSON.parse(JSON.stringify(DEFAULT_SCHEDULE));
                if (initialData.setting_times && Array.isArray(initialData.setting_times)) {
                    initialData.setting_times.forEach(item => {
                        const dayConfig = DAYS_OF_WEEK.find(d => d.value === item.week_day);
                        if (dayConfig) {
                            newSchedule[dayConfig.id] = {
                                active: true,
                                // Đảm bảo định dạng HH:mm để input type="time" hiểu được
                                start: item.hour_start ? item.hour_start.substring(0, 5) : '08:00',
                                end: item.hour_end ? item.hour_end.substring(0, 5) : '17:00'
                            };
                        }
                    });
                }
                setSchedule(newSchedule);

                // Đồng bộ danh sách Fanpage và Group đã có sẵn lên State cha
                if (initialData.fanpages) {
                    handleChangeFanpage(initialData.fanpages);
                }
                if (initialData.fanpage_groups) {
                    handleSelectGroupFanpageChange(initialData.fanpage_groups);
                }
            } else {
                // TRƯỜNG HỢP TẠO MỚI
                setName('');
                setReplyMode('always');
                setSchedule(DEFAULT_SCHEDULE);
                handleChangeFanpage([]); // Xóa các lựa chọn cũ trong state cha
                handleSelectGroupFanpageChange([]);
            }
            setError('');
        }
    }, [open, initialData, handleChangeFanpage, handleSelectGroupFanpageChange]);

    const handleScheduleChange = (dayId, field, value) => {
        setSchedule((prev) => ({
            ...prev,
            [dayId]: { ...prev[dayId], [field]: value },
        }));
    };    

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // 1. Validation cơ bản
        if (!name.trim()) {
            setError('Tên AI là bắt buộc');
            return;
        }

        

        // 2. Gom dữ liệu thời gian tùy chỉnh
        const time_type_data = [];
        if (replyMode === 'custom') {
            DAYS_OF_WEEK.forEach(day => {
                const config = schedule[day.id];
                if (config.active) {
                    time_type_data.push({
                        week_day: day.value,
                        hour_start: config.start,
                        hour_end: config.end
                    });
                }
            });

            if (time_type_data.length === 0) {
                Swal.fire({ icon: "warning", text: "Vui lòng chọn ít nhất một ngày trong tuần cho chế độ tùy chỉnh" });
                return;
            }
        }
        const selectedFanpages = state.fanpage_selected.filter(item => item.fanpage_id !== -1);
        const selectedGroups = state.selected_group_fanpage || [];        

        if (selectedFanpages.length === 0 && selectedGroups.length === 0) {
            Swal.fire({
                icon: "warning",
                text: "Vui lòng chọn ít nhất một fanpage hoặc nhóm fanpage",
            });
            return;
        }

        // 3. Tạo Payload gửi đi
        const payload = {
            name: name.trim(),
            time_type: replyMode === 'always' ? 0 : 1,
            time_type_data: time_type_data,
            // Map mảng object sang mảng ID string theo yêu cầu Backend
            fanpage_ids: selectedFanpages.map(item => String(item.fanpage_id)),
            fanpage_group_ids: selectedGroups.map(item => String(item.id))
        };

        // Nếu là edit, truyền thêm ID để backend nhận diện
        if (initialData?.id) {
            payload.id = initialData.id;
        }
        onSubmit(payload);
    };

    
    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 'bold' }}>
                {initialData ? 'Chỉnh sửa Bot AI' : 'Tạo mới Bot AI'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    {/* Tên AI */}
                    <Box sx={{ py: 1 }}>
                        <TextField
                            autoFocus
                            label="Tên Bot AI"
                            fullWidth
                            variant="outlined"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                if (e.target.value.trim()) setError('');
                            }}
                            error={!!error}
                            helperText={error}
                        />
                    </Box>

                    {/* Chế độ trả lời */}
                    <Box sx={{py: 1 }}>
                        <FormControl component="fieldset" fullWidth>
                            <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1, 
                                '&.Mui-focused': {
                                    color: 'text.primary'
                                } 
                            }}>
                                Thời gian trả lời
                            </FormLabel>
                            <RadioGroup
                                row
                                value={replyMode}
                                onChange={(e) => setReplyMode(e.target.value)}
                            >
                                <FormControlLabel value="always" control={<Radio />} label="Luôn trả lời" />
                                <FormControlLabel value="custom" control={<Radio />} label="Tuỳ chỉnh" />
                            </RadioGroup>
                        </FormControl>

                        {/* Bảng chọn lịch trình */}
                        {replyMode === 'custom' && (
                            <Box sx={{ mt: 2, pl: 1 }}>
                                {DAYS_OF_WEEK.map((day) => (
                                    <Grid container spacing={2} alignItems="center" key={day.id} sx={{ mb: 1.5 }}>
                                        <Grid item xs={4}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={schedule[day.id].active}
                                                        onChange={(e) => handleScheduleChange(day.id, 'active', e.target.checked)}
                                                    />
                                                }
                                                label={day.label}
                                            />
                                        </Grid>
                                        
                                        {schedule[day.id].active && (
                                        <>
                                            {/* Ô nhập giờ Bắt đầu */}
                                            <Grid item xs={6} md={4}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body2" sx={{ minWidth: '65px', fontWeight: 'medium' }}>
                                                     Bắt đầu:
                                                </Typography>
                                                <TextField
                                                    type="time"
                                                    size="small"
                                                    fullWidth
                                                    value={schedule[day.id].start}
                                                    onChange={(e) => handleScheduleChange(day.id, 'start', e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    // Đảm bảo trình duyệt ưu tiên hiển thị 24h (tùy thuộc locale)
                                                    inputProps={{ step: 300 }} 
                                                />
                                            </Stack>
                                            </Grid>

                                            {/* Ô nhập giờ Kết thúc */}
                                            <Grid item xs={6} md={4}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body2" sx={{ minWidth: '65px', fontWeight: 'medium' }}>
                                                    Kết thúc:
                                                </Typography>
                                                <TextField
                                                    type="time"
                                                    size="small"
                                                    fullWidth
                                                    value={schedule[day.id].end}
                                                    onChange={(e) => handleScheduleChange(day.id, 'end', e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{ step: 300 }}
                                                />
                                            </Stack>
                                            </Grid>
                                        </>
                                        )}
                                    </Grid>
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Chọn Fanpage lẻ */}
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Box sx={{display: "flex", alignItems: "center", gap: "10px" }}>
                                <DialogSelectFanpages
                                    listFanpages={state.list_fanpages}
                                    handleChangeFanpage={handleChangeFanpage}
                                    fanpageSelected={state.fanpage_selected}
                                    buttonSize='small'
                                    className="text-transform"
                                />
                                <Typography variant="body2" color={"error"}>
                                    ({state.fanpage_selected.filter((item) => item.fanpage_id !== -1).length}) {t("post.selectedFanpage")}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            {/* Chọn Nhóm Fanpage */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                {/* <SelectGroupFanpage
                                    selected_group_fanpages={state.selected_group_fanpage}
                                    onSelectGroupFanpageChange={handleSelectGroupFanpageChange}
                                    list_group_fanpage={state.list_group_fanpage}
                                /> */}
                                <DialogSelectGroupFanpages
                                    listGroupFanpages={state.list_group_fanpage}
                                    groupFanpageSelected={state.selected_group_fanpage}
                                    handleChangeGroupFanpage={handleSelectGroupFanpageChange}
                                    buttonSize='small'
                                    className="text-transform"
                                />
                                <Typography variant="body2" color={"error"}>
                                    ({state.selected_group_fanpage.filter((item) => item.fanpage_id !== -1).length}) {"Nhóm fanpage đã chọn"}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleClose} color="inherit" className="text-transform">Hủy</Button>
                    <Button type="submit" variant="contained" color="primary" className="text-transform">
                        {initialData ? 'Cập nhật' : 'Xác nhận'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}